import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminLayout from "../../components/AdminLayout";
import Pagination from "../../components/Pagination";
import { getArticles, deleteArticle, archiveArticle, API } from "../../lib/api";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { 
  Plus, 
  MoreVertical, 
  Edit2, 
  Trash2, 
  Eye,
  Sparkles,
  Search,
  Filter,
  Archive,
  ArchiveRestore,
  Star,
  Images,
  RefreshCw
} from "lucide-react";

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const params = { limit: PAGE_SIZE, skip };
      if (filterStatus) params.status = filterStatus;
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const countParams = {};
      if (filterStatus) countParams.status = filterStatus;
      if (searchTerm && searchTerm.trim()) countParams.search = searchTerm.trim();
      const [list, count] = await Promise.all([
        getArticles(params),
        axios.get(`${API}/articles/count`, {
          params: countParams,
          headers,
        }),
      ]);
      setArticles(list.data);
      setTotal(count.data?.total ?? list.data.length);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Erro ao carregar matérias");
    } finally {
      setLoading(false);
    }
  };

  // Debounce search: refetch 350ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      fetchArticles();
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, page, searchTerm]);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setPage(1);
  }, [filterStatus, searchTerm]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteArticle(deleteId);
      toast.success("Matéria excluída com sucesso");
      fetchArticles();
    } catch (error) {
      toast.error("Erro ao excluir matéria");
    } finally {
      setDeleteId(null);
    }
  };

  const handleArchive = async (article) => {
    const isArchived = article.status === "archived";
    try {
      const res = await archiveArticle(article.id);
      const newStatus = res.data?.status;
      toast.success(
        newStatus === "archived"
          ? "Matéria arquivada (oculta no portal)"
          : "Matéria desarquivada e publicada"
      );
      setArticles((prev) =>
        prev.map((a) => (a.id === article.id ? { ...a, status: newStatus || (isArchived ? "published" : "archived") } : a))
      );
    } catch (error) {
      console.error("Archive error", error);
      toast.error("Erro ao arquivar matéria");
    }
  };

  const [featurePicker, setFeaturePicker] = useState(null); // article currently choosing duration

  const setFeatured = async (article, action, days = null) => {
    try {
      const token = localStorage.getItem("token");
      const body = { action };
      if (days) body.days = days;
      const res = await axios.post(
        `${API}/articles/${article.id}/feature`,
        body,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const isFeatured = res.data?.is_featured;
      const featuredUntil = res.data?.featured_until;
      const featuredDays = res.data?.featured_days;
      if (isFeatured) {
        const msg = featuredDays
          ? `Marcada como destaque por ${featuredDays} dias`
          : "Marcada como destaque (indefinido)";
        toast.success(msg);
      } else {
        toast.success("Removida dos destaques");
      }
      setArticles((prev) =>
        prev.map((a) => (a.id === article.id ? {
          ...a,
          is_featured: isFeatured,
          featured_until: featuredUntil,
          featured_days: featuredDays,
        } : a))
      );
      setFeaturePicker(null);
    } catch (error) {
      console.error("Featured error", error);
      toast.error("Erro ao marcar destaque");
    }
  };

  const handleToggleFeatured = (article) => {
    if (article.is_featured) {
      // Already featured → unmark immediately (admin only action)
      setFeatured(article, "unmark");
    } else {
      // Not featured → open picker for duration choice
      setFeaturePicker(article);
    }
  };

  const [imageOpsLoading, setImageOpsLoading] = useState(null);

  const handleRotateImages = async () => {
    if (!window.confirm("Distribuir todas as fotos do banco entre as matérias do mesmo artista? Isso evita que duas matérias do mesmo artista fiquem com foto idêntica.")) return;
    setImageOpsLoading("rotate");
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(`${API}/admin/articles/rotate-images`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { articles_updated, artists_processed, generic_fixed, no_bank } = r.data || {};
      toast.success(`✓ ${articles_updated} matérias rotacionadas (${artists_processed} artistas, ${generic_fixed} fotos genéricas substituídas)`);
      if (no_bank?.length) {
        toast(`Sem banco de fotos: ${no_bank.slice(0, 3).join(", ")}${no_bank.length > 3 ? `… (+${no_bank.length - 3})` : ""}`, { duration: 6000 });
      }
      fetchArticles();
    } catch (e) {
      toast.error("Erro ao rotacionar fotos");
    } finally {
      setImageOpsLoading(null);
    }
  };

  const handleDedupeByImage = async () => {
    if (!window.confirm("⚠️ ATENÇÃO: isso APAGA matérias publicadas que compartilham a mesma foto, mantendo apenas a mais recente. Matérias em destaque NÃO serão apagadas. Continuar?")) return;
    setImageOpsLoading("dedupe");
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(`${API}/admin/articles/dedupe-by-image`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { deleted, duplicate_groups } = r.data || {};
      toast.success(`✓ ${deleted} matérias duplicadas removidas (${duplicate_groups} grupos)`);
      fetchArticles();
    } catch (e) {
      toast.error("Erro ao remover duplicatas");
    } finally {
      setImageOpsLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="articles-title">
              Matérias
            </h1>
            <p className="text-zinc-500 mt-1">
              Gerencie todas as matérias do portal
            </p>
          </div>
          <Link
            to="/admin/articles/create"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-xl transition-all"
            data-testid="create-article-btn"
          >
            <Plus size={20} />
            Nova Matéria
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
            <input
              type="text"
              placeholder="Buscar matérias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[#121212] border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-[#FF007F] transition-colors"
              data-testid="search-input"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-12 pr-8 py-3 bg-[#121212] border border-white/10 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-[#FF007F] transition-colors min-w-[160px]"
              data-testid="filter-select"
            >
              <option value="">Todos</option>
              <option value="published">Publicados</option>
              <option value="draft">Rascunhos</option>
              <option value="scheduled">Agendados</option>
              <option value="archived">Arquivados</option>
            </select>
          </div>
        </div>

        {/* Image management toolbar */}
        <div className="flex flex-wrap gap-2 -mt-2">
          <button
            onClick={handleRotateImages}
            disabled={!!imageOpsLoading}
            data-testid="rotate-images-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/15 hover:bg-blue-500/30 disabled:opacity-50 text-blue-300 hover:text-blue-100 font-bold text-sm rounded-lg border border-blue-500/30 transition-all"
            title="Distribui as fotos do banco entre matérias do mesmo artista (não apaga nada)"
          >
            {imageOpsLoading === "rotate" ? <RefreshCw size={16} className="animate-spin" /> : <Images size={16} />}
            {imageOpsLoading === "rotate" ? "Rotacionando..." : "Rotacionar fotos (sem apagar)"}
          </button>
          <button
            onClick={handleDedupeByImage}
            disabled={!!imageOpsLoading}
            data-testid="dedupe-images-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/15 hover:bg-red-500/30 disabled:opacity-50 text-red-300 hover:text-red-100 font-bold text-sm rounded-lg border border-red-500/30 transition-all"
            title="APAGA matérias com fotos repetidas (mantém a mais recente). Destaques são preservados."
          >
            {imageOpsLoading === "dedupe" ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
            {imageOpsLoading === "dedupe" ? "Apagando..." : "Apagar duplicatas por foto"}
          </button>
        </div>

        {/* Table */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-2 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-zinc-500">Nenhuma matéria encontrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Matéria</TableHead>
                  <TableHead className="text-zinc-400">Categoria</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400">Views</TableHead>
                  <TableHead className="text-zinc-400">Data</TableHead>
                  <TableHead className="text-zinc-400 w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {articles.map((article) => (
                  <TableRow 
                    key={article.id} 
                    className="border-white/5 hover:bg-white/5"
                    data-testid={`article-row-${article.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img
                          src={article.image_url || "https://images.unsplash.com/photo-1635989193974-102e31f3db9b?w=100"}
                          alt=""
                          className="w-12 h-9 object-cover rounded-lg"
                        />
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate max-w-[300px]">
                            {article.title}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-zinc-400">{article.category}</span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        article.status === "published" 
                          ? "bg-green-500/20 text-green-500" 
                          : article.status === "scheduled"
                          ? "bg-blue-500/20 text-blue-500"
                          : article.status === "archived"
                          ? "bg-zinc-500/20 text-zinc-400"
                          : "bg-amber-500/20 text-amber-500"
                      }`}>
                        {article.status === "published"
                          ? "Publicado"
                          : article.status === "scheduled"
                          ? "Agendado"
                          : article.status === "archived"
                          ? "Arquivado"
                          : "Rascunho"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-zinc-400 flex items-center gap-1">
                        <Eye size={14} />
                        {article.views}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-zinc-400">{formatDate(article.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggleFeatured(article)}
                          title={
                            article.is_featured
                              ? (article.featured_until
                                  ? `Em destaque até ${new Date(article.featured_until).toLocaleDateString("pt-BR")} — clique para remover`
                                  : "Em destaque (indefinido) — clique para remover")
                              : "Marcar como destaque"
                          }
                          className={`p-2 rounded-lg transition-colors ${
                            article.is_featured
                              ? "text-amber-400 bg-amber-500/15 hover:bg-amber-500/25"
                              : "text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10"
                          }`}
                          data-testid={`feature-btn-${article.id}`}
                        >
                          <Star size={16} fill={article.is_featured ? "currentColor" : "none"} />
                        </button>
                        <button
                          onClick={() => handleArchive(article)}
                          title={article.status === "archived" ? "Desarquivar" : "Arquivar"}
                          className="p-2 hover:bg-amber-500/10 text-amber-400 rounded-lg transition-colors"
                          data-testid={`archive-btn-${article.id}`}
                        >
                          {article.status === "archived" ? (
                            <ArchiveRestore size={16} />
                          ) : (
                            <Archive size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteId(article.id)}
                          title="Excluir"
                          className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg transition-colors"
                          data-testid={`delete-btn-${article.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                        <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                            <MoreVertical size={16} className="text-zinc-400" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-white/10">
                          <DropdownMenuItem asChild>
                            <Link 
                              to={`/artigo/${article.id}`}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <Eye size={16} />
                              Visualizar
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link 
                              to={`/admin/articles/edit/${article.id}`}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <Edit2 size={16} />
                              Editar
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="flex items-center gap-2 text-amber-400 cursor-pointer"
                            onClick={() => handleArchive(article)}
                            data-testid={`archive-article-${article.id}`}
                          >
                            {article.status === "archived" ? (
                              <>
                                <ArchiveRestore size={16} />
                                Desarquivar
                              </>
                            ) : (
                              <>
                                <Archive size={16} />
                                Arquivar
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="flex items-center gap-2 text-red-500 cursor-pointer"
                            onClick={() => setDeleteId(article.id)}
                            data-testid={`delete-article-${article.id}`}
                          >
                            <Trash2 size={16} />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          page={page}
          totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
          onChange={setPage}
        />
        {total > 0 && (
          <p className="text-zinc-500 text-sm text-center -mt-6">
            Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total} matérias
          </p>
        )}
      </div>

      {/* Feature duration picker */}
      <AlertDialog open={!!featurePicker} onOpenChange={(open) => !open && setFeaturePicker(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10" data-testid="feature-picker-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Star size={20} className="text-amber-400" fill="currentColor" /> Marcar como destaque
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Por quanto tempo essa matéria deve ficar em destaque na home?<br/>
              <span className="text-zinc-500 text-xs">Apenas o admin pode remover o destaque depois.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2 my-4">
            {[
              { label: "7 dias", days: 7 },
              { label: "14 dias", days: 14 },
              { label: "30 dias", days: 30 },
              { label: "Indefinido", days: null },
            ].map((opt) => (
              <button
                key={opt.label}
                onClick={() => featurePicker && setFeatured(featurePicker, "mark", opt.days)}
                data-testid={`feature-duration-${opt.days || "indefinite"}`}
                className="px-4 py-3 bg-amber-500/15 hover:bg-amber-500/30 text-amber-300 hover:text-amber-200 font-bold rounded-lg border border-amber-500/30 transition-all"
              >
                {opt.label}
              </button>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-white/10 text-white hover:bg-white/10"
              data-testid="feature-picker-cancel"
            >
              Cancelar
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir matéria?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta ação não pode ser desfeita. A matéria será permanentemente excluída.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/10" data-testid="confirm-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-action"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
